import moment from "moment-timezone";
import { nanoid } from "nanoid";

import * as api from "@/apiv2/timesheets";
import * as alertActions from "@/redux/alerts/alerts.actions";
import * as customersSelectors from "@/redux/customers/customers.selectors";
import * as selectors from "@/redux/timesheets/timesheets.selectors";
import { handleError } from "@/redux/utils/error";
import { STATUS } from "@/utils/constants";
import * as date from "@/utils/date";

import { resolveFileUrl } from "../../env";

import {
  RESET,
  SET_ACTIVE_USER,
  SET_DATES,
  SET_FROM_DATE,
  SET_IS_DOWNLOADING_TIMESHEET_STATUS,
  SET_IS_FETCHING_SUMMARY_STATUS,
  SET_IS_SUBMITTING_TIMESHEET_TO_ACCOUNTING_STATUS,
  SET_IS_UPDATING_TIMESHEET_STATUS,
  SET_MODAL_TYPE,
  SET_SELECTED_USER_IDS,
  SET_SUMMARY,
  SET_TIME_TRACKING_TYPE,
  SET_TO_DATE,
} from "./timesheets.actionTypes";

export const reset = () => (dispatch) => {
  dispatch({
    type: RESET,
  });
};

export const setIsFetchingSummaryStatus = (data) => (dispatch) => {
  dispatch({
    type: SET_IS_FETCHING_SUMMARY_STATUS,
    payload: data,
  });
};

export const setIsUpdatingTimesheetStatus = (data) => (dispatch) => {
  dispatch({
    type: SET_IS_UPDATING_TIMESHEET_STATUS,
    payload: data,
  });
};

export const isDownloadingTimesheetStatus = (data) => (dispatch) => {
  dispatch({
    type: SET_IS_DOWNLOADING_TIMESHEET_STATUS,
    payload: data,
  });
};

export const isSubmittingTimesheetToAccountingStatus = (data) => (dispatch) => {
  dispatch({
    type: SET_IS_SUBMITTING_TIMESHEET_TO_ACCOUNTING_STATUS,
    payload: data,
  });
};

export const setSummary = (data) => (dispatch) => {
  dispatch({
    type: SET_SUMMARY,
    payload: data,
  });
};

export const setFromDate = (data) => (dispatch) => {
  dispatch({
    type: SET_FROM_DATE,
    payload: data,
  });
};

export const setToDate = (data) => (dispatch) => {
  dispatch({
    type: SET_TO_DATE,
    payload: data,
  });
};

export const setDates = (data) => (dispatch) => {
  dispatch({
    type: SET_DATES,
    payload: data,
  });
};

export const setModalType = (data) => (dispatch) => {
  dispatch({
    type: SET_MODAL_TYPE,
    payload: data,
  });
};

export const setSelectedUserIds = (data) => (dispatch) => {
  dispatch({
    type: SET_SELECTED_USER_IDS,
    payload: data,
  });
};

export const setActiveUser = (data) => (dispatch) => {
  dispatch({
    type: SET_ACTIVE_USER,
    payload: data,
  });
};

export const setTimeTrackingType = (data) => (dispatch) => {
  dispatch({
    type: SET_TIME_TRACKING_TYPE,
    payload: data,
  });
};

export const fetchSummary = () => async (dispatch, getState) => {
  try {
    const state = getState();
    const fromDate = selectors.getFromDate(state);
    const toDate = selectors.getToDate(state);
    const activeUser = selectors.getActiveUser(state);
    const timeTrackingType = selectors.getTimeTrackingType(state);
    const company = customersSelectors.companySelector(state);

    // Note: (This is temporary a condition while waiting for QBO Payroll Integration) Automatically determine if we should use grouped view based on accounting system
    const shouldUseGroupedView =
      company?.accountingName === "QBO" ||
      company?.accountingName === "None" ||
      company.accountingName === "MYOB" ||
      company.accountingName === "Xero";

    dispatch(setIsFetchingSummaryStatus(STATUS.PENDING));
    const { data } = await api.getTimesheetSummary({
      // fromDate/toDate already hold the exact 7 days shown on the page. Do not
      // snap them out to a Sunday-Saturday week: when the company starts its week
      // on any other day, that snapping asks the API for 14 days instead of 7.
      start_date: moment(fromDate).format("YYYY-MM-DD"),
      end_date: moment(toDate).format("YYYY-MM-DD"),
      user_id: activeUser?.id ?? undefined,
      timezone: date.getCurrentUserTimezone(),
      ...(shouldUseGroupedView && { is_grouped: 1, time_tracking_type: timeTrackingType }),
    })();

    // Handle different response formats
    const processedSummary = shouldUseGroupedView
      ? // For grouped response, keep the object structure but convert date keys
        data.map((s) => {
          const processedTimesheet = Object.keys(s.timesheet).reduce((acc, dateKey) => {
            const localDate = moment(dateKey, "YYYYMMDDTHH:mm:ssZ").format("YYYY-MM-DD");
            acc[localDate] = s.timesheet[dateKey];
            return acc;
          }, {});

          return {
            ...s,
            timesheet: processedTimesheet,
          };
        })
      : // For standard response, convert to array format
        data.map((s) => ({
          ...s,
          timesheet: Object.keys(s.timesheet).map((t) => ({
            ...s.timesheet[t],
            date: date.toLocalDateTime({
              date: moment(t, "YYYYMMDDTHH:mm:ssZ").format("YYYY-MM-DDTHH:mm:ssZ"),
              timezone: date.getCurrentUserTimezone(),
              format: "YYYY-MM-DD",
            }),
          })),
        }));

    dispatch(setSummary(processedSummary));

    dispatch(setIsFetchingSummaryStatus(STATUS.FULFILLED));
  } catch (error) {
    dispatch(setIsFetchingSummaryStatus(STATUS.REJECTED));
    handleError(error, dispatch);
  }
};

export const bulkChangeStatusOfTimeEntries =
  ({ userIds, timeEntriesUids, status, triggerPlace = "" }) =>
  async (dispatch, getState) => {
    const state = getState();
    const summary = selectors.getSummary(state);

    const updatedSummaryState = summary.map((s) => {
      if (userIds.includes(s.userId)) {
        // Handle both grouped view (object) and standard view (array) formats
        const isGroupedView = !Array.isArray(s.timesheet);

        if (isGroupedView) {
          // For grouped view, timesheet is an object with date keys, each containing type objects
          const updatedTimesheet = {};
          Object.keys(s.timesheet).forEach((dateKey) => {
            const dayData = s.timesheet[dateKey];
            updatedTimesheet[dateKey] = {};

            // Handle each tracking type (work, attendance, breaks)
            Object.keys(dayData).forEach((trackingType) => {
              const typeData = dayData[trackingType];
              const updatedTimeEntries = typeData.timeEntries
                ? typeData.timeEntries.map((t) => {
                    if (timeEntriesUids.includes(t.uid)) {
                      return {
                        ...t,
                        approvalStatus: status,
                      };
                    }
                    return t;
                  })
                : [];

              // Calculate the actual status based on the time entries' approval statuses
              const calculatedStatus = (() => {
                if (updatedTimeEntries.length === 0) {
                  return "";
                }

                const statuses = updatedTimeEntries.map((t) => t.approvalStatus);
                const uniqueStatuses = [...new Set(statuses)];

                if (uniqueStatuses.length === 1) {
                  return uniqueStatuses[0];
                }
                if (uniqueStatuses.length > 1) {
                  return "multiple_statuses";
                }

                return "";
              })();

              updatedTimesheet[dateKey][trackingType] = {
                ...typeData,
                status: calculatedStatus,
                timeEntries: updatedTimeEntries,
              };
            });
          });

          return {
            ...s,
            timesheet: updatedTimesheet,
          };
        }
        // For standard view, timesheet is an array
        return {
          ...s,
          timesheet: s.timesheet.map((timesheet) => {
            const updatedTimeEntries = timesheet.timeEntries.map((t) => {
              if (timeEntriesUids.includes(t.uid)) {
                return {
                  ...t,
                  approvalStatus: status,
                };
              }
              return t;
            });

            // Calculate the actual status based on the time entries' approval statuses
            const calculatedStatus = (() => {
              if (updatedTimeEntries.length === 0) {
                return "";
              }

              const statuses = updatedTimeEntries.map((t) => t.approvalStatus);
              const uniqueStatuses = [...new Set(statuses)];

              if (uniqueStatuses.length === 1) {
                return uniqueStatuses[0];
              }
              if (uniqueStatuses.length > 1) {
                return "multiple_statuses";
              }

              return "";
            })();

            return {
              ...timesheet,
              status: calculatedStatus,
              timeEntries: updatedTimeEntries,
            };
          }),
        };
      }

      return s;
    });

    dispatch(setSummary(updatedSummaryState));

    try {
      const payload = {
        uid: timeEntriesUids,
        status,
      };

      dispatch(
        setIsUpdatingTimesheetStatus({
          [triggerPlace === "header" ? "header" : userIds[0]]: {
            status,
            loading: STATUS.PENDING,
          },
        })
      );

      await api.patchBulkChangeTimeEntryStatus({ data: payload })();

      dispatch(
        setIsUpdatingTimesheetStatus({
          [triggerPlace === "header" ? "header" : userIds[0]]: {
            status,
            loading: STATUS.FULFILLED,
          },
        })
      );

      dispatch(
        alertActions.createAlertAction(nanoid(), `Timesheets marked as ${status}`, true, "success")
      );

      return updatedSummaryState;
    } catch (error) {
      handleError(error, dispatch);
    }
  };

export const downloadTimeEntries =
  ({ timeEntriesUids }) =>
  async (dispatch) => {
    try {
      const payload = {
        uid: timeEntriesUids,
      };

      dispatch(isDownloadingTimesheetStatus(STATUS.PENDING));

      const { data } = await api.postDownloadTimesheets({ data: payload })();
      dispatch(alertActions.createAlertAction(nanoid(), `Timesheets downloaded`, true, "success"));
      dispatch(isDownloadingTimesheetStatus(STATUS.FULFILLED));
      return resolveFileUrl(data);
    } catch (error) {
      handleError(error, dispatch);
    }
  };

export const exportTimeEntriesToAccountingPackage =
  ({ userIds, startDate, endDate, timeTrackingType }) =>
  async (dispatch) => {
    try {
      const payload = {
        users: userIds,
        startDate,
        endDate,
        timeTrackingType,
      };

      dispatch(isSubmittingTimesheetToAccountingStatus(STATUS.PENDING));
      await api.postExportAllTimesheets({ data: payload })();
      dispatch(
        alertActions.createAlertAction(
          nanoid(),
          `Timesheets exported successfully`,
          true,
          "success"
        )
      );
    } catch (error) {
      handleError(error, dispatch);
    } finally {
      dispatch(isSubmittingTimesheetToAccountingStatus(STATUS.FULFILLED));
    }
  };

export const setPreviousWeekDates = () => (dispatch, getState) => {
  const state = getState();
  const fromDate = selectors.getFromDate(state);
  const weekStartDay = customersSelectors.getWeekCommencesOn(state);

  const fromDatePreviousWeek = moment(fromDate).subtract(1, "weeks").day(weekStartDay);
  const datesOfPreviousWeek = Array.from({ length: 7 }, (_, i) =>
    fromDatePreviousWeek.clone().add(i, "days").format("YYYY-MM-DD")
  );

  dispatch(setDates(datesOfPreviousWeek));

  dispatch(setFromDate(datesOfPreviousWeek[0]));

  dispatch(setToDate(datesOfPreviousWeek.at(-1)));
};

export const setNextWeekDates = () => (dispatch, getState) => {
  const state = getState();
  const fromDate = selectors.getFromDate(state);
  const weekStartDay = customersSelectors.getWeekCommencesOn(state);

  const fromDateNextWeek = moment(fromDate).add(1, "weeks").day(weekStartDay);
  const datesOfNextWeek = Array.from({ length: 7 }, (_, i) =>
    fromDateNextWeek.clone().add(i, "days").format("YYYY-MM-DD")
  );

  dispatch(setDates(datesOfNextWeek));

  dispatch(setFromDate(datesOfNextWeek[0]));

  dispatch(setToDate(datesOfNextWeek.at(-1)));
};
